import { useEffect, RefObject } from 'react';
import { setFocusMode } from 't-lib-ui/structure';
import { findNearestHeadlineItem } from './HeadlineListFocus';
import { findNearestSidebarItem } from './LeftSidebarFocus';

const LINE_SCROLL_AMOUNT = 80;

function isFullyOnScreen(el: HTMLElement, containerRect: DOMRect): boolean {
  const r = el.getBoundingClientRect();
  return r.top >= containerRect.top && r.bottom <= containerRect.bottom &&
         r.left >= containerRect.left && r.right <= containerRect.right &&
         r.width > 0 && r.height > 0;
}

function isVisible(el: HTMLElement): boolean {
  const style = getComputedStyle(el);
  return style.display != 'none' && style.visibility != 'hidden' && style.opacity != '0';
}

function hasVisibleModal(): boolean {
  return Array.from(document.querySelectorAll<HTMLElement>('.flowFormModal'))
    .some(el => el.offsetParent || el.matches(':modal'));
}

function getScrollContainer(postView: HTMLElement): HTMLElement | null {
  return postView.querySelector('.postsContainer');
}

/** Focus the end-of-list action button (preferring the "Next Feed" / "Home" button)
 *  so keyboard users land on it once they reach the bottom — mirrors the behaviour of
 *  pressing 'j' at the last post. Returns false when no such button is visible. */
function focusEndOfListButton(postView: HTMLElement): boolean {
  const btns = Array.from(postView.querySelectorAll<HTMLElement>('.markAllReadBottomButton.flowFocussable:not([disabled])'));
  const btn = btns.find(el => el.offsetParent && el.closest('.endOfUnreadNextFeed'))
    ?? btns.find(el => el.offsetParent);
  if (!btn) return false;
  setFocusMode('visible');
  btn.focus({ preventScroll: true });
  return true;
}

function getAllFocusable(container: HTMLElement): HTMLElement[] {
  return Array.from(container.querySelectorAll<HTMLElement>('.flowFocussable'))
    .filter(el => isVisible(el));
}

function getNonSidewayFocusable(container: HTMLElement): HTMLElement[] {
  return Array.from(container.querySelectorAll<HTMLElement>('.flowFocussable:not(.sidewayFocus)'))
    .filter(el => isVisible(el));
}

/** Find the next non-sideway focusable element in DOM order */
function findNextInDomOrder(current: HTMLElement, direction: 1 | -1, container: HTMLElement, sideways: boolean): HTMLElement | null {
  const elements = sideways ? getAllFocusable(container) : getNonSidewayFocusable(container);
  const idx = elements.indexOf(current);
  if (idx < 0) {
    // Current element not in list - find closest by DOM position
    if (direction == 1) return elements[0] ?? null;
    return elements[elements.length - 1] ?? null;
  }
  const next = elements[idx + direction];
  return next ?? null;
}

const MAX_SPATIAL_VERTICAL_DISTANCE = 50;

/** Find the closest focusable element in a spatial direction (left/right) that is fully on screen and within vertical proximity */
function findClosestSpatial(current: HTMLElement, direction: 'left' | 'right', container: HTMLElement, containerRect: DOMRect): HTMLElement | null {
  const elements = getAllFocusable(container)
    .filter(el => el !== current && isFullyOnScreen(el, containerRect));

  const startRect = current.getBoundingClientRect();
  let best: HTMLElement | null = null;
  let bestDist = Infinity;

  for (const el of elements) {
    const r = el.getBoundingClientRect();

    // Check vertical proximity: element must be within 50px vertically
    const verticalGap = Math.max(0, r.top - startRect.bottom, startRect.top - r.bottom);
    if (verticalGap > MAX_SPATIAL_VERTICAL_DISTANCE) continue;

    // Check direction
    if (direction == 'right' && r.left <= startRect.right) continue;
    if (direction == 'left' && r.right >= startRect.left) continue;

    const dist = direction == 'right'
      ? r.left - startRect.right
      : startRect.left - r.right;

    if (dist < bestDist) {
      bestDist = dist;
      best = el;
    }
  }

  return best;
}

/** Find the next non-sideway focusable element below the current one that is fully on screen */
function findNextOnScreenBelow(current: HTMLElement, container: HTMLElement, containerRect: DOMRect): HTMLElement | null {
  const elements = getNonSidewayFocusable(container);
  const idx = elements.indexOf(current);

  // Search forward in DOM order for an element that is below and fully on screen
  const startIdx = idx >= 0 ? idx + 1 : 0;
  for (let i = startIdx; i < elements.length; i++) {
    const el = elements[i];
    if (el.getBoundingClientRect().top > current.getBoundingClientRect().top && isFullyOnScreen(el, containerRect))
      return el;
  }
  return null;
}

/** Find the next non-sideway focusable element above the current one that is fully on screen */
function findNextOnScreenAbove(current: HTMLElement, container: HTMLElement, containerRect: DOMRect): HTMLElement | null {
  const elements = getNonSidewayFocusable(container);
  const idx = elements.indexOf(current);

  // Search backward in DOM order for an element that is above and fully on screen
  const startIdx = idx >= 0 ? idx - 1 : elements.length - 1;
  for (let i = startIdx; i >= 0; i--) {
    const el = elements[i];
    if (el.getBoundingClientRect().bottom < current.getBoundingClientRect().bottom && isFullyOnScreen(el, containerRect))
      return el;
  }
  return null;
}


export function usePostViewFocus(postViewRef: RefObject<HTMLDivElement | null>) {
  useEffect(() => {
    const postView = postViewRef.current;
    if (!postView) return;

    function mouseHandler() {
      setFocusMode('hidden');
    }

    function handleKeyDown(ev: KeyboardEvent) {
      if (ev.defaultPrevented) return;
      if (hasVisibleModal()) return;

      const focused = document.activeElement as HTMLElement;

      // Only handle if focus is within this postView or on body
      if (!postView!.contains(focused) && focused !== document.body) return;

      const shift = ev.shiftKey;
      const key = ev.key;

      // Space / Shift+Space: scroll the post content (but let buttons handle Space as click)
      if (key == ' ') {
        const active = focused.tagName;
        if (active == 'INPUT' || active == 'TEXTAREA' || focused.isContentEditable) return;
        if (active == 'BUTTON') return; // let native button Space behavior through
        const sc = getScrollContainer(postView!);
        if (!sc) return;
        ev.preventDefault();
        ev.stopPropagation();
        const direction = ev.shiftKey ? -1 : 1;
        // Forward Space at the very bottom: focus the Next Feed / Home button instead of
        // scrolling nowhere — same as pressing 'j' at the last post.
        const atBottom = sc.scrollTop + sc.clientHeight >= sc.scrollHeight - 8;
        if (direction == 1 && atBottom && focusEndOfListButton(postView!)) return;
        sc.dispatchEvent(new Event('cancelscroll'));
        sc.scrollBy({ top: sc.clientHeight * 0.9 * direction, behavior: 'smooth' });
        return;
      }

      // Only handle arrow keys
      if (key != 'ArrowDown' && key != 'ArrowUp' && key != 'ArrowLeft' && key != 'ArrowRight') return;

      setFocusMode('visible');

      const scrollContainer = getScrollContainer(postView!);
      const containerRect = postView!.getBoundingClientRect();

      // --- Shift + Arrow: standard FlowForm DOM-order navigation ---
      if (shift && key == 'ArrowDown') {
        const next = findNextInDomOrder(focused, 1, postView!, false);
        if (next) {
          ev.preventDefault();
          ev.stopPropagation();
          next.focus();
        }
        return;
      }
      if (shift && key == 'ArrowUp') {
        const prev = findNextInDomOrder(focused, -1, postView!, false);
        if (prev) {
          ev.preventDefault();
          ev.stopPropagation();
          prev.focus();
        }
        return;
      }
      if (shift && key == 'ArrowRight') {
        const next = findNextInDomOrder(focused, 1, postView!, true);
        if (next) {
          ev.preventDefault();
          ev.stopPropagation();
          next.focus();
        }
        return;
      }
      if (shift && key == 'ArrowLeft') {
        const prev = findNextInDomOrder(focused, -1, postView!, true);
        if (prev) {
          ev.preventDefault();
          ev.stopPropagation();
          prev.focus();
        }
        return;
      }

      // --- ArrowLeft / ArrowRight ---
      // Header: DOM-order navigation (same as Shift+Arrow). Content: spatial navigation.
      // If no DOM target found for ArrowLeft, fall through to cross-panel navigation below.
      if ((key == 'ArrowLeft' || key == 'ArrowRight') && (!scrollContainer || !scrollContainer.contains(focused))) {
        const dir = key == 'ArrowRight' ? 1 : -1;
        const next = findNextInDomOrder(focused, dir, postView!, true);
        if (next) {
          ev.preventDefault();
          ev.stopPropagation();
          next.focus();
          return;
        }
        if (key == 'ArrowRight') return;
        // ArrowLeft with no target: fall through to cross-panel
      }

      if (key == 'ArrowLeft') {
        const target = findClosestSpatial(focused, 'left', postView!, containerRect);
        if (target) {
          ev.preventDefault();
          ev.stopPropagation();
          target.focus({ preventScroll: true });
          return;
        }
        // No spatial target in PostView - try cross-panel to HeadlineList, then LeftSidebar (single-column mode)
        const isInHeader = !scrollContainer || !scrollContainer.contains(focused);
        const crossPanel = isInHeader
          ? findNearestSidebarItem(focused)
          : findNearestHeadlineItem(focused) ?? findNearestSidebarItem(focused);
        if (crossPanel) {
          ev.preventDefault();
          ev.stopPropagation();
          crossPanel.focus({ preventScroll: true });
        }
        return;
      }
      if (key == 'ArrowRight') {
        const target = findClosestSpatial(focused, 'right', postView!, containerRect);
        if (target) {
          ev.preventDefault();
          ev.stopPropagation();
          target.focus({ preventScroll: true });
        }
        return;
      }

      // --- ArrowDown: scroll + viewport-based focus ---
      if (key == 'ArrowDown') {
        if (!scrollContainer) return;
        ev.preventDefault();
        ev.stopPropagation();

        const isInHeader = !scrollContainer.contains(focused);
        const atBottom = scrollContainer.scrollTop + scrollContainer.clientHeight >= scrollContainer.scrollHeight - 1;

        if (isInHeader) {
          // Try DOM-order navigation within header first
          const next = findNextInDomOrder(focused, 1, postView!, false);
          if (next && !scrollContainer.contains(next)) {
            next.focus({ preventScroll: true });
            return;
          }
          // Focus first fully visible non-sideway element in content, fall back to first partially visible
          const elements = getNonSidewayFocusable(scrollContainer);
          const scrollRect = scrollContainer.getBoundingClientRect();
          const first = elements.find(el => isFullyOnScreen(el, scrollRect))
            ?? elements.find(el => { const r = el.getBoundingClientRect(); return r.bottom > scrollRect.top && r.top < scrollRect.bottom && r.height > 0; });
          if (first) first.focus({ preventScroll: true });
          return;
        }

        if (atBottom) {
          // Can't scroll further down, but still try to move focus down
          const next = findNextOnScreenBelow(focused, postView!, containerRect);
          if (next) next.focus({ preventScroll: true });
          return;
        }

        // Cap scroll amount to distance to next focusable element
        const focusedRect = focused.getBoundingClientRect();
        const nextInDom = findNextInDomOrder(focused, 1, postView!, false);
        const maxScroll = nextInDom ? Math.max(0, nextInDom.getBoundingClientRect().top - focusedRect.top) : LINE_SCROLL_AMOUNT;
        const scrollAmount = Math.min(LINE_SCROLL_AMOUNT, maxScroll || LINE_SCROLL_AMOUNT);
        scrollContainer.scrollTo({ top: scrollContainer.scrollTop + scrollAmount, behavior: ev.repeat ? 'instant' : 'smooth' });

        // If focused element scrolled off screen, focus next visible element immediately
        if (!isFullyOnScreen(focused, containerRect)) {
          const next = findNextOnScreenBelow(focused, postView!, containerRect);
          if (next) next.focus({ preventScroll: true });
        }
        // If focused element is in upper 85% and next element also in upper 85% → jump
        else {
          const threshold = containerRect.top + containerRect.height * 0.85;
          if (focusedRect.bottom <= threshold) {
            const next = findNextOnScreenBelow(focused, postView!, containerRect);
            if (next && next.getBoundingClientRect().top <= threshold)
              next.focus({ preventScroll: true });
          }
        }
        return;
      }

      // --- ArrowUp: scroll + viewport-based focus ---
      if (key == 'ArrowUp') {
        if (!scrollContainer) return;
        ev.preventDefault();
        ev.stopPropagation();

        const isInHeader = !scrollContainer.contains(focused);
        if (isInHeader) {
          const prev = findNextInDomOrder(focused, -1, postView!, false);
          if (prev && !scrollContainer.contains(prev)) {
            ev.preventDefault();
            ev.stopPropagation();
            prev.focus({ preventScroll: true });
          }
          return;
        }

        const atTop = scrollContainer.scrollTop <= 0;
        if (atTop) {
          // Try to move focus up within content first, then jump to first header element
          const prev = findNextOnScreenAbove(focused, postView!, containerRect);
          if (prev) { prev.focus({ preventScroll: true }); return; }
          const headerEls = getAllFocusable(postView!).filter(el => !scrollContainer!.contains(el) && isVisible(el));
          if (headerEls.length) headerEls[0].focus({ preventScroll: true });
          return;
        }

        // Cap scroll amount to distance to prev focusable element
        const focusedRect = focused.getBoundingClientRect();
        const prevInDom = findNextInDomOrder(focused, -1, postView!, false);
        const maxScroll = prevInDom ? Math.max(0, focusedRect.top - prevInDom.getBoundingClientRect().top) : LINE_SCROLL_AMOUNT;
        const scrollAmount = Math.min(LINE_SCROLL_AMOUNT, maxScroll || LINE_SCROLL_AMOUNT);
        scrollContainer.scrollTo({ top: scrollContainer.scrollTop - scrollAmount, behavior: ev.repeat ? 'instant' : 'smooth' });

        // If focused element scrolled off screen, focus prev visible element immediately
        if (!isFullyOnScreen(focused, containerRect)) {
          const prev = findNextOnScreenAbove(focused, postView!, containerRect);
          if (prev) prev.focus({ preventScroll: true });
        }
        // If focused element is in lower 85% and prev element also in lower 85% → jump
        else {
          const threshold = containerRect.top + containerRect.height * 0.15;
          if (focusedRect.top >= threshold) {
            const prev = findNextOnScreenAbove(focused, postView!, containerRect);
            if (prev && prev.getBoundingClientRect().bottom >= threshold)
              prev.focus({ preventScroll: true });
          }
        }
        return;
      }
    }

    document.addEventListener('keydown', handleKeyDown);
    postView.addEventListener('mousedown', mouseHandler);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      postView.removeEventListener('mousedown', mouseHandler);
    };
  }, [postViewRef]);
}
